<template>
  <div>
    <member-content-title :title="$t('personal_information_title')" />

    <div class="wd-flex wd-flex-col wd-gap-1">
      <MemberSingleFieldForm
        v-for="field in personalInfoList"
        :key="field.key"
        v-model:editing-key="editingKey"
        :field="field"
        :value="userInfo?.[field.key] ?? null"
        :schema="personalInfoSchema[field.key]"
      />

      <MemberPhoneField :phone="userInfo?.phone ?? null" :disabled="editingKey !== ''" />
    </div>

    <ui-modal v-model="isNavigationWarningShow" :header-title="$t('edit_not_done_modal_title')" :forcing="true">
      <p class="!wd-mb-0 wd-p-5 xs:wd-pb-10 wd-text-omd wd-text-black50">{{ $t('edit_not_done_modal_content') }}</p>

      <template #buttons>
        <div class="wd-flex wd-px-5 wd-py-3 xs:wd-py-6 wd-gap-3 xs:wd-gap-4 xs:wd-justify-end">
          <v-btn class="wd-text-otempmd wd-text-black50 wd-flex-1 xs:wd-flex-none" :height="40" color="gray20" variant="flat" @click="handleDiscard">
            {{ $t('create_account_cancel_modal_cancel_button') }}
          </v-btn>
          <v-btn class="wd-text-otempmd wd-flex-1 xs:wd-flex-none" :height="40" color="orange50" variant="flat" @click="handleKeepEditing">
            {{ $t('edit_not_done_modal_continue_button') }}
          </v-btn>
        </div>
      </template>
    </ui-modal>
  </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import * as Yup from 'yup'
import type { AnySchema } from 'yup'
import type { RouteLocationNormalized } from 'vue-router'

import { useMemberPageAuth } from '@/composition/useMemberPageAuth'
import { useUserInfo } from '@/composition/useMember'

import UiModal from '@/components/dialog/ui-modal.vue'
import MemberContentTitle from '@/components/member/MemberContentTitle.vue'
import MemberSingleFieldForm from '@/components/member/MemberSingleFieldForm.vue'
import MemberTextInput from '@/components/member/MemberTextInput.vue'
import MemberSelectInput from '@/components/member/MemberSelectInput.vue'
import MemberBirthdayInput from '@/components/member/MemberBirthdayInput.vue'
import MemberPhoneField from '@/components/member/MemberPhoneField.vue'

import { Gender, MemberForm, genderI18nKeyMap } from '@/view-models/member'
import type { MemberFormItem } from '@/view-models/member'
import { MEMBER_BIRTHDAY_AGE_DEFAULT, isValidMemberBirthdayInput } from '@/utils/memberBirthday'

definePageMeta({
  layout: 'member',
})

const { t } = useI18n()
const router = useRouter()
const { userInfo } = useUserInfo()
const rules = useFieldRules()

useMemberPageAuth()

const editingKey = ref('')
const isNavigationWarningShow = ref(false)
const pendingRoute = ref<RouteLocationNormalized | null>(null)

const birthdayDefaultView = dayjs().subtract(MEMBER_BIRTHDAY_AGE_DEFAULT, 'year')

const personalInfoList = computed<MemberFormItem[]>(() => [
  {
    key: MemberForm.NICKNAME,
    titleText: t('PersonalVC_yourName'),
    type: 'text',
    component: MemberTextInput,
  },
  {
    key: MemberForm.GENDER,
    titleText: t('PersonalVC_gender'),
    type: 'select',
    component: MemberSelectInput,
    isNeedComplete: true,
    componentProps: {
      options: Object.entries(genderI18nKeyMap)
        .filter(([key]) => Number(key) !== Gender.UNSPECIFIED)
        .map(([key, value]) => ({ label: t(value), value: Number(key) })),
    },
    getDefaultValue: value => (value === Gender.UNSPECIFIED ? null : value),
  },
  {
    key: MemberForm.BIRTHDAY,
    titleText: t('PersonalVC_birthday'),
    type: 'date',
    component: MemberBirthdayInput,
    isNeedComplete: true,
    isEditable: !!userInfo.value?.bhd_editable,
    note: userInfo.value?.bhd_editable ? t('member_birthday_warning') : t('member_birthday_set_hint'),
    componentProps: { birthdayDefaultView },
    getDefaultValue: value => value || birthdayDefaultView.format('YYYY-MM-DD'),
  },
  {
    key: MemberForm.EMAIL,
    titleText: t('all_email'),
    type: 'text',
    component: MemberTextInput,
    note: t('profile_email_hint'),
  },
])

const personalInfoSchema = computed<Record<MemberForm, AnySchema>>(() => ({
  [MemberForm.NICKNAME]: rules.wordLimit(t('credit_card_error')),
  [MemberForm.GENDER]: Yup.number().required(t('credit_card_error')),
  [MemberForm.BIRTHDAY]: Yup.string()
    .required(t('credit_card_error'))
    .test('birthday-valid', t('credit_card_error'), value => isValidMemberBirthdayInput(value)),
  [MemberForm.EMAIL]: rules.email(t('profile_email_format_error'), t('profile_email_invalid_format')),
}))

const isNeedPageLeaveCheck = computed(() => editingKey.value !== '')

onBeforeRouteLeave((to, _from, next) => {
  if (!isNeedPageLeaveCheck.value) {
    next()
    return
  }
  pendingRoute.value = to
  isNavigationWarningShow.value = true
  next(false)
})

function handleDiscard() {
  isNavigationWarningShow.value = false
  editingKey.value = ''
  if (pendingRoute.value) {
    router.push(pendingRoute.value)
  }
}

function handleKeepEditing() {
  isNavigationWarningShow.value = false
}
</script>
